import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { segnaTappa } from "@/components/VisitaOspite";
import api from "@/lib/api";
import axios from "axios";
import { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { photoUrl, fileUrl, DEFAULT_EMPLOYEE_PHOTO } from "@/lib/placeholders";
import TesserinoSchermo from "@/components/TesserinoSchermo";
import {
  Calendar, ShieldCheck, CheckCircle2, AlertTriangle, Clock, ExternalLink,
  HardHat, PenLine, ChevronDown, FileSignature,
} from "lucide-react";

const itDate = (iso) => (iso ? iso.substring(0, 10).split("-").reverse().join("/") : "");

// Pagina aperta scansionando il QR del tesserino di cantiere: nessun login.
// Mostra solo quel che serve a un preposto per decidere se la persona puo'
// lavorare. Recapiti, documenti d'identita' e idoneita' sanitarie non arrivano
// nemmeno dal server: si consultano dal gestionale, previo accesso.
const STATUS_META = {
  valido: { label: "In corso di validità", icon: CheckCircle2, box: "border-emerald-200 bg-emerald-50", badge: "bg-emerald-600 text-white" },
  in_scadenza: { label: "In scadenza", icon: Clock, box: "border-amber-200 bg-amber-50", badge: "bg-amber-500 text-white" },
  scaduto: { label: "Scaduto", icon: AlertTriangle, box: "border-red-200 bg-red-50", badge: "bg-red-600 text-white" },
  senza_scadenza: { label: "Senza scadenza", icon: CheckCircle2, box: "border-slate-200 bg-slate-50", badge: "bg-slate-500 text-white" },
};

// Chi, entrando da dentro l'app, ha diritto al fascicolo vero e non a questa
// pagina ridotta. Il preposto no: in cantiere legge il QR e gli basta sapere
// se la persona può lavorare.
const VEDE_IL_FASCICOLO_INTERNO = ["admin", "amministrativo", "tecnico"];

export default function Fascicolo() {
  const { id } = useParams();
  const { user } = useAuth();
  const vaiA = useNavigate();

  // Questa pagina nasce per il QR del tesserino, senza login. Ma dal
  // gestionale ci si arriva anche cliccando "fascicolo dipendente", e chi è
  // già dentro con i permessi giusti non deve trovarsi la versione ridotta:
  // gli serve il fascicolo con i documenti, da cui può anche lavorarci.
  //
  // Salvo quando il tesserino e' proprio quello che si vuole: «Mostra
  // tesserino» dal QR o dal cantiere arriva con ?tesserino=1 e resta qui.
  const [tesserinoVoluto] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("tesserino") === "1"; } catch { return false; }
  });
  // La tessera vera e propria, in sovrimpressione sopra il riepilogo.
  const [mostraTessera, setMostraTessera] = useState(tesserinoVoluto);
  useEffect(() => {
    if (!tesserinoVoluto && user && VEDE_IL_FASCICOLO_INTERNO.includes(user.role)) {
      vaiA(`/dipendenti?fascicolo=${id}`, { replace: true });
    }
  }, [user, id, vaiA, tesserinoVoluto]);

  // La tendina dei DPI: chiusa finché non la si apre, così la pagina resta
  // corta anche per chi ha trent'anni di consegne alle spalle.
  const [dpiAperti, setDpiAperti] = useState(false);

  // Se a leggere questo QR e' un ospite in visita, la tappa entra nel suo
  // giro. Per tutti gli altri non succede niente.
  useEffect(() => { segnaTappa("dipendente", id); }, [id]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/employees/${id}`).then(r => setData(r.data))
      .catch((e) => {
        if (e.response?.status === 401 || e.response?.status === 403) {
          // Il tesserino non e' piu' una pagina aperta: chi non ha un account
          // ne' una visita in corso passa dal codice, e poi torna qui.
          window.location.replace(
            `/visita?vai=${encodeURIComponent(window.location.pathname)}`);
          return;
        }
        setError("Tesserino non riconosciuto");
      });
  }, [id]);

  if (error) return <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
    <Card><CardContent className="p-8 text-center">
      <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
      <h2 className="font-display text-2xl font-bold">{error}</h2>
    </CardContent></Card>
  </div>;

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Caricamento...</div>;

  const { employee, certificates } = data;
  const dpi = data.dpi || [];
  // Il modulo di consegna firmato non è un attestato di sicurezza: sta con i
  // DPI, che è la domanda a cui risponde.
  const attestati = certificates.filter((c) => c.specie !== "consegna_dpi");
  const moduliDpi = certificates.filter((c) => c.specie === "consegna_dpi");
  const scaduti = attestati.filter(c => c.status === "scaduto");
  const inScadenza = attestati.filter(c => c.status === "in_scadenza");
  const regolare = scaduti.length === 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {mostraTessera && <TesserinoSchermo employeeId={id} onChiudi={() => setMostraTessera(false)} />}
      <header className="bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-2 bg-warning-stripe" />
        <div className="max-w-3xl mx-auto px-6 py-8 relative">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4">
              <img src={photoUrl(employee.photo_url) || DEFAULT_EMPLOYEE_PHOTO} alt=""
                   className="w-20 h-20 rounded object-cover border-2 border-white/20 flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-slate-400">Tesserino di riconoscimento</div>
                <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">{employee.full_name}</h1>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-300">
                  <Badge className="bg-orange-500 text-white border-0">{employee.role}</Badge>
                  {employee.badge_number && <span>Matricola: {employee.badge_number}</span>}
                  <button type="button" onClick={() => setMostraTessera(true)} data-testid="apri-tessera"
                          className="text-xs font-semibold text-white bg-white/15 hover:bg-white/25 rounded px-2 py-1">
                    Mostra la tessera
                  </button>
                </div>
              </div>
            </div>
            <div className="text-xs text-slate-400 text-right">
              <div>{data.company}</div>
              <div>Verifica attestati di sicurezza</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-5">
        <Card className={regolare ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}>
          <CardContent className="p-5 flex items-center gap-3">
            {regolare
              ? <ShieldCheck className="w-8 h-8 text-emerald-600 flex-shrink-0" />
              : <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0" />}
            <div>
              <div className={`font-display text-lg font-bold ${regolare ? "text-emerald-800" : "text-red-800"}`}>
                {regolare ? "Attestati in regola" : `${scaduti.length} attestat${scaduti.length === 1 ? "o scaduto" : "i scaduti"}`}
              </div>
              <div className="text-sm text-slate-600">
                {regolare
                  ? (inScadenza.length > 0
                      ? `Attenzione: ${inScadenza.length} in scadenza entro 30 giorni.`
                      : "Nessuna scadenza imminente.")
                  : "Verificare con l'ufficio prima di ammettere la persona in cantiere."}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-orange-500" /> Attestati di sicurezza
            </h2>
            {attestati.length === 0 ? (
              <p className="text-sm text-slate-500">Nessun attestato registrato per questa persona.</p>
            ) : (
              <div className="space-y-2">
                {attestati.map(c => {
                  const meta = STATUS_META[c.status] || STATUS_META.senza_scadenza;
                  const Icon = meta.icon;
                  return (
                    <div key={c.id} className={`p-3 rounded border ${meta.box} flex items-center gap-3`}>
                      <Icon className="w-4 h-4 flex-shrink-0 text-slate-600" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{c.title}</div>
                        <div className="text-xs text-slate-500">
                          {c.category}
                          {c.expires_date && (
                            <> · <Calendar className="w-3 h-3 inline" /> scadenza {c.expires_date.substring(0, 10).split("-").reverse().join("/")}</>
                          )}
                        </div>
                      </div>
                      {c.file_url && (
                        <a href={fileUrl(c.file_url)} target="_blank" rel="noopener noreferrer"
                           data-testid={`apri-attestato-${c.id}`}
                           className="text-orange-600 text-xs font-medium flex items-center gap-1 flex-shrink-0">
                          apri <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <Badge className={`${meta.badge} border-0 text-[10px] flex-shrink-0`}>{meta.label}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* L'altra cosa che il coordinatore verifica sempre: che i DPI siano
            stati dati, e che la persona li abbia presi in carico firmando.
            E' un adempimento nostro da dimostrare, non un dato riservato suo,
            quindi sta qui accanto agli attestati. */}
        <Card className="border-slate-200" data-testid="elenco-dpi">
          <button type="button" onClick={() => setDpiAperti((a) => !a)}
                  data-testid="tendina-dpi"
                  className="w-full p-6 flex items-center gap-2 text-left">
            <HardHat className="w-5 h-5 text-orange-500 flex-shrink-0" />
            <span className="font-display text-xl font-bold">DPI ultimo anno</span>
            <span className="text-xs text-slate-500">
              {dpi.length} {dpi.length === 1 ? "consegna" : "consegne"}
            </span>
            <ChevronDown className={`w-5 h-5 text-slate-400 ml-auto flex-shrink-0 transition-transform ${
              dpiAperti ? "rotate-180" : ""}`} />
          </button>
          {dpiAperti && (
          <CardContent className="px-6 pb-6 pt-0">
            {moduliDpi.length > 0 && (
              <div className="mb-3 space-y-1">
                {moduliDpi.map((m) => (
                  <a key={m.id} href={fileUrl(m.file_url)} target="_blank"
                     rel="noopener noreferrer" data-testid={`modulo-dpi-${m.id}`}
                     className="flex items-center gap-2 text-sm p-2 rounded border border-orange-200 bg-orange-50 text-orange-800">
                    <FileSignature className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 min-w-0 truncate">{m.title}</span>
                    <span className="text-xs font-medium flex items-center gap-1 flex-shrink-0">
                      apri <ExternalLink className="w-3 h-3" />
                    </span>
                  </a>
                ))}
              </div>
            )}
            {dpi.length === 0 ? (
              <p className="text-sm text-slate-500">
                Nessuna consegna registrata nell&apos;ultimo anno.
              </p>
            ) : (
              <div className="max-h-[26rem] overflow-y-auto -mx-2 px-2 space-y-2">
                {dpi.map((c) => (
                  <div key={c.id} className="border border-slate-200 rounded p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          {itDate(c.data)}
                        </div>
                        <ul className="mt-1 space-y-0.5">
                          {c.righe.map((r, i) => (
                            <li key={i} className="text-sm text-slate-700">
                              {r.quantita > 1 && <span className="tabular-nums">{r.quantita}× </span>}
                              {r.articolo}
                              {r.taglia && <span className="text-slate-400"> · taglia {r.taglia}</span>}
                            </li>
                          ))}
                        </ul>
                        {c.consegnato_da && (
                          <div className="text-[11px] text-slate-400 mt-1">
                            consegnati da {c.consegnato_da}
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-center">
                        {c.firma ? (
                          <>
                            <img src={c.firma} alt="firma di presa in carico"
                                 className="h-12 w-28 object-contain bg-white border border-slate-200 rounded" />
                            <div className="text-[10px] text-emerald-600 mt-0.5 flex items-center justify-center gap-1">
                              <PenLine className="w-3 h-3" /> presa in carico
                            </div>
                          </>
                        ) : (
                          <div className="text-[10px] text-slate-400 w-28">
                            storico da registro cartaceo, senza firma digitale
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
          )}
        </Card>

        <p className="text-center text-xs text-slate-400 py-4">
          {data.company} · Questa pagina riporta gli attestati di sicurezza e i DPI consegnati.
          <br />Per il fascicolo completo occorre accedere al gestionale aziendale.
        </p>
      </main>
    </div>
  );
}
